import { useEffect, useState } from "react";
import { recordDebug } from "./debug";

const SAMPLES = 5;
const RESYNC_MS = 5 * 60 * 1000;

/**
 * One NTP-style reading against `/time` (functions/time.ts): assumes the
 * server read its clock halfway through the round trip.
 */
const sample = async () => {
  const sentAt = Date.now();
  const start = performance.now();
  const res = await fetch("/time", { cache: "no-store" });
  const { now } = (await res.json()) as { now: number };
  const roundTrip = performance.now() - start;
  return { offset: now - (sentAt + roundTrip / 2), roundTrip };
};

/**
 * Milliseconds to add to `Date.now()` to get server time. Takes a few
 * readings and keeps the fastest round trip, whose midpoint guess is the most
 * accurate, then resyncs every few minutes and whenever the page comes back.
 * Stays 0 where there's no `/time` (e.g. `yarn start`).
 */
export const useServerClockOffset = (enabled: boolean) => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const sync = async () => {
      let best: { offset: number; roundTrip: number } | undefined;
      let failedSamples = 0;
      for (let i = 0; i < SAMPLES && !cancelled; i++) {
        try {
          const reading = await sample();
          if (!best || reading.roundTrip < best.roundTrip) best = reading;
        } catch (e) {
          // Offline or no endpoint; keep the last offset.
          failedSamples++;
        }
      }
      if (best && !cancelled) {
        setOffset(Math.round(best.offset));
        recordDebug({
          clock: {
            offsetMs: Math.round(best.offset),
            roundTripMs: best.roundTrip,
            syncedAt: Date.now(),
            failedSamples,
          },
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") sync();
    };

    sync();
    const interval = window.setInterval(sync, RESYNC_MS);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled]);

  return offset;
};
